/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Autoplay preference.
 *
 * Mirrors GH's AutoplayModal setting (`queueStore.autoplayEnabled`):
 * when ON, playback keeps going with machine-picked similar tracks after
 * the picked queue ends. GH persists this locally with no GraphQL
 * mutation; we do the same — the flag is read at end-of-queue in
 * {@link @/playback/autoplayContinuation}, which re-seeds a fresh
 * continuation queue (matching GH's `_handlePlayerQueueEnded`).
 *
 * Defaults ON, matching GH's `queueStore.autoplayEnabled` (`?? true`);
 * the Settings page Autoplay row lets a user turn it off.
 */

import type { ObservableValue } from '@amazon/vinyl-observable'
import { persistedToggle } from '@/util/persistedToggle'

/** Persistence key for the autoplay toggle. `true` = autoplay on. */
export const AUTOPLAY_ENABLED_KEY = 'autoplayEnabled'

const autoplay = persistedToggle(AUTOPLAY_ENABLED_KEY, true)

/**
 * Observable: `true` when autoplay continuation should be enabled for new
 * queues. Subscribe from the settings toggle so the UI reflects flips.
 */
export const autoplayEnabled$: ObservableValue<boolean> = autoplay.enabled$

/** Snapshot: `true` when autoplay is enabled. */
export function isAutoplayEnabled(): boolean {
    return autoplay.enabled$.value
}

/** Persist the toggle and update {@link autoplayEnabled$} immediately. */
export const writeAutoplayEnabled = autoplay.write
